import React, { useState } from 'react';
import { Edit, Loader } from 'lucide-react';
import { aiAPI } from '../services/api';
import toast from 'react-hot-toast';
import MarkdownRenderer from './MarkdownRenderer';
import StatsAndTabs from './StatsAndTabs';

const DocumentDraft = ({ onSuccess, stats, loading: statsLoading }) => {
  const [formData, setFormData] = useState({
    documentType: '',
    requirements: ''
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.documentType.trim() || !formData.requirements.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    setResult('');

    try {
      const response = await aiAPI.draftDocument({
        documentType: formData.documentType,
        requirements: formData.requirements
      });

      setResult(response.result);
      toast.success('Document draft generated!');
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Document drafting failed:', error);
      toast.error('Failed to generate document draft');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFormData({
      documentType: '',
      requirements: ''
    });
    setResult('');
  };

  return (
    <div>
      <div className="content-header">
        <div className="welcome-section">
          <h2>Document Draft</h2>
          <p>Generate legal document drafts with AI assistance</p>
        </div>
      </div>

      <StatsAndTabs stats={stats} loading={statsLoading} />

      <div className="tool-content">
        <div className="tool-header">
          <Edit size={24} />
          <h3>Document Drafter</h3>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Document Type</label>
            <input
              type="text"
              name="documentType"
              className="form-input"
              placeholder="Enter document type (e.g., contract, agreement, letter)..."
              value={formData.documentType}
              onChange={handleChange}
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Requirements & Details</label>
            <textarea
              name="requirements"
              className="form-textarea"
              placeholder="Enter specific requirements, terms, and details for the document..."
              value={formData.requirements}
              onChange={handleChange}
              disabled={loading}
              style={{ minHeight: '150px' }}
            />
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader size={16} className="animate-spin" />
                  Drafting Document...
                </>
              ) : (
                <>
                  <Edit size={16} />
                  Generate Draft
                </>
              )}
            </button>

            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleClear}
              disabled={loading}
            >
              Clear
            </button>
          </div>
        </form>
      </div>

      {result && (
        <div className="tool-content">
          <div className="tool-header">
            <Edit size={24} />
            <h3>Document Draft</h3>
          </div>
          
          <MarkdownRenderer content={result} />

          <div style={{ marginTop: '16px' }}>
            <button
              className="btn btn-secondary"
              onClick={() => navigator.clipboard.writeText(result)}
            >
              Copy Draft
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentDraft;
